// Story 2.4 — mutation hook for POST
// /api/v1/admin/drops/:drop_id/transition.
//
// Class-based `AdminDropTransitionError` exposes the AppError's `code`
// discriminator + the field echoes the cockpit page uses to branch its
// toast copy + dialog state per failure mode:
//
//   * DROP_TRANSITION_INVALID — 422, carries current_status +
//     requested_to_status + allowed_to_status (the legal next state, or
//     null for terminal); the cockpit refetches the list so the SPA
//     can re-render with the post-race state.
//   * DROP_TRANSITION_CONFIRMATION_MISMATCH — 400, terminal transition
//     missing or wrong typed-confirmation; the cockpit's ForceCloseDialog
//     keeps the input field visible + shakes-and-clears.
//   * DROP_TRANSITION_WELCOME_FORBIDDEN — 422, is_perpetual=true; the
//     SPA shouldn't surface action buttons for welcome rows anyway, but
//     this guards against direct API misuse.
//   * DROP_NOT_FOUND — 404, the drop_id no longer exists (e.g., admin
//     opened the page hours ago, drop was hard-deleted in the interim).
//
// Generates `X-Idempotency-Key` per request so a network-retry replay
// returns the cached envelope from the FINANCIAL_MUTATION_PREFIXES
// middleware (the prefix /api/v1/admin/drops covers this subpath via
// starts_with matching).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type DropTransitionTarget = "live" | "retiring" | "retired";

export type TransitionDropRequest = {
  drop_id: string;
  to_status: DropTransitionTarget;
  confirmation_token?: string;
};

export type TransitionDropOutcome = {
  drop_id: string;
  portfolio_id: string;
  kind: string;
  status: string;
  from_status: string;
  to_status: string;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  retired_at: string | null;
  transitioned_at: string;
};

export type TransitionDropResponse = {
  data: TransitionDropOutcome;
};

export class AdminDropTransitionError extends Error {
  code: string;
  status: number;
  dropId?: string;
  currentStatus?: string;
  requestedToStatus?: string;
  allowedToStatus?: string | null;
  expectedHint?: string;

  constructor(params: {
    message: string;
    code: string;
    status: number;
    dropId?: string;
    currentStatus?: string;
    requestedToStatus?: string;
    allowedToStatus?: string | null;
    expectedHint?: string;
  }) {
    super(params.message);
    this.name = "AdminDropTransitionError";
    this.code = params.code;
    this.status = params.status;
    this.dropId = params.dropId;
    this.currentStatus = params.currentStatus;
    this.requestedToStatus = params.requestedToStatus;
    this.allowedToStatus = params.allowedToStatus;
    this.expectedHint = params.expectedHint;
  }
}

function generateIdempotencyKey(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof (crypto as Crypto).randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `transition-drop-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

async function postTransitionDrop(
  request: TransitionDropRequest,
): Promise<TransitionDropResponse> {
  const body: { to_status: string; confirmation_token?: string } = {
    to_status: request.to_status,
  };
  if (request.confirmation_token !== undefined) {
    body.confirmation_token = request.confirmation_token;
  }
  const response = await fetch(
    `/api/v1/admin/drops/${encodeURIComponent(request.drop_id)}/transition`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Idempotency-Key": generateIdempotencyKey(),
      },
      body: JSON.stringify(body),
    },
  );
  attachRequestIdToScope(response);

  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Drop transition failed (HTTP ${response.status})`;
    let dropId: string | undefined;
    let currentStatus: string | undefined;
    let requestedToStatus: string | undefined;
    let allowedToStatus: string | null | undefined;
    let expectedHint: string | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.code) code = raw.error.code;
      if (raw?.error?.message) message = raw.error.message;
      if (raw?.error?.drop_id) dropId = raw.error.drop_id;
      if (raw?.error?.current_status)
        currentStatus = raw.error.current_status;
      if (raw?.error?.requested_to_status)
        requestedToStatus = raw.error.requested_to_status;
      if (raw?.error?.allowed_to_status !== undefined)
        allowedToStatus = raw.error.allowed_to_status;
      if (raw?.error?.expected_hint) expectedHint = raw.error.expected_hint;
    } catch {
      // body not JSON
    }
    throw new AdminDropTransitionError({
      message,
      code,
      status: response.status,
      dropId,
      currentStatus,
      requestedToStatus,
      allowedToStatus,
      expectedHint,
    });
  }

  return (await response.json()) as TransitionDropResponse;
}

export function useAdminTransitionDrop() {
  const queryClient = useQueryClient();
  return useMutation<
    TransitionDropResponse,
    AdminDropTransitionError,
    TransitionDropRequest
  >({
    mutationFn: postTransitionDrop,
    onSuccess: (data) => {
      // Invalidate the cockpit list (all filter variations).
      queryClient.invalidateQueries({ queryKey: ["admin-drops-list"] });
      // Also invalidate the Story 2.3 detail-page query if the admin
      // came from there — the panel's status badge needs to refresh.
      queryClient.invalidateQueries({
        queryKey: ["admin-drop-for-portfolio", data.data.portfolio_id],
      });
    },
  });
}
